import { Injectable, effect, inject, signal, untracked } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { routerCancelAction, routerNavigatedAction } from '@ngrx/router-store'
import { Store } from '@ngrx/store'

import {
  delay,
  filter,
  map,
  of,
  pairwise,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import {
  AiAnalyticsAction,
  AiAnalyticsObject,
  AiAnalyticsSessionFeedbackEvent,
  AiAnalyticsSessionFeedbackParams,
  AiAnalyticsSessionFinishedEvent,
  AiAnalyticsSessionFinishedParams,
  AiAnalyticsSessionStartedEvent,
  AiAnalyticsSessionStartedParams,
} from '@flowx/designer/modules/analytics/analytics-ai.entity'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'

import { AiActions, CommandPaletteActions, NodeMenuActions } from './ai.actions'
import { AiSelectors } from './ai.selectors'

import { showModalAboveAiPanel } from '../../../shared/utils/modal.utils'
import { AiAssistantActions } from '../agents/assistant/store/assistant.actions'
import { AiDocumentsApi } from '../agents/documents/api/documents.api'
import { AiDocumentsTransformService } from '../agents/documents/services/ai-documents-transform.service'
import { AiDocumentsActions } from '../agents/documents/store/documents.actions'
import { AiFeedbackModalComponent } from '../modules/chat/components/ai-feedback-modal/ai-feedback-modal.component'
import { FeedbackType } from '../types/ai.types'

@Injectable()
export class AiEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiContextApi = inject(AiDocumentsApi)
  #modalService = inject(FlxModalService)
  #analyticsService = inject(AnalyticsService)

  #hasPrevRequest = false
  #previousAnalyticsSessionData = signal({ agent: null, session_id: null, session_length: 0 })

  onAskAssistantCommand = createEffect(() =>
    this.#actions$.pipe(
      ofType(CommandPaletteActions.askAssistantCommand),
      map(({ question }) => AiAssistantActions.getAnswer({ prompt: question }))
    )
  )

  onFromDocumentsClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.fromDocumentsClick),
      map(() => AiDocumentsActions.loadDocuments())
    )
  )

  loadDocuments = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDocumentsActions.loadDocuments),
      withLatestFrom(this.#store.select(AiSelectors.selectedAgent)),
      switchMap(([, selectedAgent]) =>
        this.#aiContextApi.getDocuments(selectedAgent).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((documents) =>
            AiDocumentsActions.loadDocumentsSuccess({
              documents: AiDocumentsTransformService.contextDocumentResponseTransform(
                documents
              ).filter((document) => document[selectedAgent].length > 0),
            })
          )
        )
      )
    )
  )


  onFeedbackClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.feedbackClick),
      switchMap(({ requestId, feedbackType }) => {
        if (feedbackType === FeedbackType.NEGATIVE) {
          const modal = showModalAboveAiPanel(this.#modalService, AiFeedbackModalComponent, {
            appendTo: 'body',
          })
          return modal.data$.pipe(
            take(1),
            filter(({ confirm }) => !!confirm),
            map(({ reason, feedback }) =>
              AiActions.sendFeedback({
                feedback: {
                  requestId,
                  feedbackType,
                  formData: { issue: reason, details: feedback },
                },
              })
            )
          )
        }
        return of(AiActions.sendFeedback({ feedback: { requestId, feedbackType } }))
      })
    )
  )

  onSelectedAgentReset = createEffect(() =>
    this.#store.select(AiSelectors.selectedAgent).pipe(
      pairwise(),
      withLatestFrom(
        this.#store.select(AiSelectors.pendingAiResponse),
        this.#store.select(AiSelectors.isUploadingDocuments)
      ),
      filter(
        ([[prev, curr], pendingAiResponse, isUploadingDocuments]) =>
          prev !== curr && (pendingAiResponse || isUploadingDocuments)
      ),
      map(() => AiActions.cancelPendingRequest())
    )
  )

  onPageAgentChanged = createEffect(() =>
    this.#actions$.pipe(ofType(routerNavigatedAction, routerCancelAction)).pipe(
      switchMap(() => this.#store.select(AiSelectors.pageAgent).pipe(take(1))),
      pairwise(),
      filter(([prev, curr]) => prev !== curr),
      map(() => AiActions.pageAgentChanged())
    )
  )

  onGenerateUiNodeMenuAction = createEffect(() =>
    this.#actions$.pipe(
      ofType(NodeMenuActions.generateUiNodeMenuAction),
      switchMap(({ userTaskNavigationAreaId }) =>
        this.#actions$.pipe(ofType(routerNavigatedAction)).pipe(
          take(1),
          delay(0), // wait for page agent change action
          map(() =>
            NodeMenuActions.generateUiNodeMenuActionTransitionEnd({ userTaskNavigationAreaId })
          )
        )
      )
    )
  )

  onCancelPendingRequestClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.cancelPendingRequestClick),
      map(() => AiActions.cancelPendingRequest())
    )
  )

  analyticsStartSessionEvents = effect(() => {
    const agent = this.#store.selectSignal(AiSelectors.selectedAgent)()
    const session_id = this.#store.selectSignal(AiSelectors.sessionId)()
    const session_length = this.#store.selectSignal(AiSelectors.messages)().length ?? 0

    const currData = {
      agent,
      session_id,
      session_length,
    }
    let prevData = null
    untracked(() => {
      prevData = this.#previousAnalyticsSessionData()
    })

    if (!prevData.agent && currData.agent) {
      const { agent, session_id } = currData

      this.#analyticsService.trackAI(
        {
          object: AiAnalyticsObject.AI,
          action: AiAnalyticsAction.SESSION_STARTED,
        } satisfies AiAnalyticsSessionStartedEvent,
        { agent, session_id } satisfies AiAnalyticsSessionStartedParams
      )
    } else if (prevData.agent && !currData.agent) {
      const { agent, session_id, session_length } = prevData

      this.#analyticsService.trackAI(
        {
          object: AiAnalyticsObject.AI,
          action: AiAnalyticsAction.SESSION_FINISHED,
        } satisfies AiAnalyticsSessionFinishedEvent,
        {
          agent,
          session_id,
          session_length,
          outcome: 'none',
        } satisfies AiAnalyticsSessionFinishedParams
      )
    }

    this.#previousAnalyticsSessionData.set(currData)
  })

  analyticsSessionFeedbackEvents = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiActions.feedbackClick),
        withLatestFrom(
          this.#store.select(AiSelectors.selectedAgent),
          this.#store.select(AiSelectors.sessionId),
          this.#store.select(AiSelectors.messages)
        ),
        tap(([{ feedbackType }, selectedAgent, sessionId, messages]) => {
          this.#analyticsService.trackAI(
            {
              object: AiAnalyticsObject.AI,
              action: AiAnalyticsAction.SESSION_FEEDBACK,
            } satisfies AiAnalyticsSessionFeedbackEvent,
            {
              agent: selectedAgent,
              session_id: sessionId,
              session_length: messages.length,
              feedback: feedbackType === FeedbackType.POSITIVE ? 'happy' : 'unhappy',
            } satisfies AiAnalyticsSessionFeedbackParams
          )
        })
      ),
    { dispatch: false }
  )
}
